// eslint-disable-next-line max-classes-per-file
import { Application } from 'app/application';
import { ContextService } from '../services/context-service';

// Copyied from https://github.com/Microsoft/vscode/blob/ec1ca5ec204c42757ac5cbc53a3e250116fb5658/src/vs/platform/contextkey/common/contextkey.ts
/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

/* eslint-disable @typescript-eslint/no-explicit-any */
export function isFalsyOrWhitespace(str: string | undefined): boolean {
    if (!str || typeof str !== 'string') {
        return true;
    }
    return str.trim().length === 0;
}

export enum ContextKeyExprType {
    Defined = 1,
    Not = 2,
    Equals = 3,
    NotEquals = 4,
    And = 5,
    Regex = 6
}

export abstract class ContextKeyExpr {
    public static has(key: string): ContextKeyExpr {
        return new ContextKeyDefinedExpr(key);
    }

    public static equals(key: string, value: any): ContextKeyExpr {
        return new ContextKeyEqualsExpr(key, value);
    }

    public static notEquals(key: string, value: any): ContextKeyExpr {
        return new ContextKeyNotEqualsExpr(key, value);
    }

    public static regex(key: string, value: RegExp): ContextKeyExpr {
        return new ContextKeyRegexExpr(key, value);
    }

    public static not(key: string): ContextKeyExpr {
        return new ContextKeyNotExpr(key);
    }

    public static and(...expr: Array<ContextKeyExpr | undefined | null>): ContextKeyExpr {
        return new ContextKeyAndExpr(expr);
    }

    public static deserialize(serialized: string | null | undefined): ContextKeyExpr | null {
        if (!serialized) {
            return null;
        }

        const pieces = serialized.split('&&');
        const result = new ContextKeyAndExpr(pieces.map((p) => this._deserializeOne(p)));
        return result.normalize();
    }

    private static _deserializeOne(serializedOne: string): ContextKeyExpr {
        serializedOne = serializedOne.trim();

        if (serializedOne.indexOf('!=') >= 0) {
            const pieces = serializedOne.split('!=');
            return new ContextKeyNotEqualsExpr(pieces[0].trim(), this._deserializeValue(pieces[1]));
        }

        if (serializedOne.indexOf('==') >= 0) {
            const pieces = serializedOne.split('==');
            return new ContextKeyEqualsExpr(pieces[0].trim(), this._deserializeValue(pieces[1]));
        }

        if (serializedOne.indexOf('=~') >= 0) {
            const pieces = serializedOne.split('=~');
            return new ContextKeyRegexExpr(pieces[0].trim(), this._deserializeRegexValue(pieces[1]));
        }

        if (/^\!\s*/.test(serializedOne)) {
            return new ContextKeyNotExpr(serializedOne.substr(1).trim());
        }

        return new ContextKeyDefinedExpr(serializedOne);
    }

    private static _deserializeValue(serializedValue: string): any {
        serializedValue = serializedValue.trim();

        if (serializedValue === 'true') {
            return true;
        }

        if (serializedValue === 'false') {
            return false;
        }

        const m = /^"([^"]*)"$/.exec(serializedValue);
        if (m) {
            return m[1].trim();
        }

        return serializedValue;
    }

    private static _deserializeRegexValue(serializedValue: string): RegExp | null {

        if (isFalsyOrWhitespace(serializedValue)) {
            console.warn('missing regexp-value for =~-expression');
            return null;
        }

        const start = serializedValue.indexOf('/');
        const end = serializedValue.lastIndexOf('/');
        if (start === end || start < 0 /* || to < 0 */) {
            console.warn(`bad regexp-value "${serializedValue}", missing /-enclosure`);
            return null;
        }

        const value = serializedValue.slice(start + 1, end);
        const caseIgnoreFlag = serializedValue[end + 1] === 'i' ? 'i' : '';
        try {
            return new RegExp(value, caseIgnoreFlag);
        } catch (e) {
            console.warn(`bad regexp-value "${serializedValue}", parse error: ${e}`);
            return null;
        }
    }

    public abstract getType(): ContextKeyExprType;
    public abstract equals(other: ContextKeyExpr): boolean;
    public abstract evaluate(context: IContext): boolean;
    public abstract normalize(): ContextKeyExpr | null;
    public abstract serialize(): string;
    public abstract keys(): string[];
}

function cmp(a: ContextKeyExpr, b: ContextKeyExpr): number {
    const aType = a.getType();
    const bType = b.getType();
    if (aType !== bType) {
        return aType - bType;
    }
    switch (aType) {
        case ContextKeyExprType.Defined:
            return (a as ContextKeyDefinedExpr).cmp(b as ContextKeyDefinedExpr);
        case ContextKeyExprType.Not:
            return (a as ContextKeyNotExpr).cmp(b as ContextKeyNotExpr);
        case ContextKeyExprType.Equals:
            return (a as ContextKeyEqualsExpr).cmp(b as ContextKeyEqualsExpr);
        case ContextKeyExprType.NotEquals:
            return (a as ContextKeyNotEqualsExpr).cmp(b as ContextKeyNotEqualsExpr);
        case ContextKeyExprType.Regex:
            return (a as ContextKeyRegexExpr).cmp(b as ContextKeyRegexExpr);
        default:
            throw new Error('Unknown ContextKeyExpr!');
    }
}

export class ContextKeyDefinedExpr implements ContextKeyExpr {
    constructor(protected key: string) {
    }

    public getType(): ContextKeyExprType {
        return ContextKeyExprType.Defined;
    }

    public cmp(other: ContextKeyDefinedExpr): number {
        if (this.key < other.key) {
            return -1;
        }
        if (this.key > other.key) {
            return 1;
        }
        return 0;
    }

    public equals(other: ContextKeyExpr): boolean {
        if (other instanceof ContextKeyDefinedExpr) {
            return (this.key === other.key);
        }
        return false;
    }

    public evaluate(context: IContext): boolean {
        return (!!context.getValue(this.key));
    }

    public normalize(): ContextKeyExpr {
        return this;
    }

    public serialize(): string {
        return this.key;
    }

    public keys(): string[] {
        return [this.key];
    }
}

export class ContextKeyEqualsExpr implements ContextKeyExpr {
    constructor(private key: string, private value: any) {
    }

    public getType(): ContextKeyExprType {
        return ContextKeyExprType.Equals;
    }

    public cmp(other: ContextKeyEqualsExpr): number {
        if (this.key < other.key) {
            return -1;
        }
        if (this.key > other.key) {
            return 1;
        }
        if (this.value < other.value) {
            return -1;
        }
        if (this.value > other.value) {
            return 1;
        }
        return 0;
    }

    public equals(other: ContextKeyExpr): boolean {
        if (other instanceof ContextKeyEqualsExpr) {
            return (this.key === other.key && this.value === other.value);
        }
        return false;
    }

    public evaluate(context: IContext): boolean {
        // Intentional ==
        // eslint-disable-next-line eqeqeq
        return (context.getValue(this.key) == this.value);
    }

    public normalize(): ContextKeyExpr {
        if (typeof this.value === 'boolean') {
            if (this.value) {
                return new ContextKeyDefinedExpr(this.key);
            }
            return new ContextKeyNotExpr(this.key);
        }
        return this;
    }

    public serialize(): string {
        if (typeof this.value === 'boolean') {
            return this.normalize().serialize();
        }

        return this.key + ' == "' + this.value + '"';
    }

    public keys(): string[] {
        return [this.key];
    }
}

export class ContextKeyNotEqualsExpr implements ContextKeyExpr {
    constructor(private key: string, private value: any) {
    }

    public getType(): ContextKeyExprType {
        return ContextKeyExprType.NotEquals;
    }

    public cmp(other: ContextKeyNotEqualsExpr): number {
        if (this.key < other.key) {
            return -1;
        }
        if (this.key > other.key) {
            return 1;
        }
        if (this.value < other.value) {
            return -1;
        }
        if (this.value > other.value) {
            return 1;
        }
        return 0;
    }

    public equals(other: ContextKeyExpr): boolean {
        if (other instanceof ContextKeyNotEqualsExpr) {
            return (this.key === other.key && this.value === other.value);
        }
        return false;
    }

    public evaluate(context: IContext): boolean {
        // Intentional !=
        // eslint-disable-next-line eqeqeq
        return (context.getValue(this.key) != this.value);
    }

    public normalize(): ContextKeyExpr {
        if (typeof this.value === 'boolean') {
            if (this.value) {
                return new ContextKeyNotExpr(this.key);
            }
            return new ContextKeyDefinedExpr(this.key);
        }
        return this;
    }

    public serialize(): string {
        if (typeof this.value === 'boolean') {
            return this.normalize().serialize();
        }

        return this.key + ' != "' + this.value + '"';
    }

    public keys(): string[] {
        return [this.key];
    }
}

export class ContextKeyNotExpr implements ContextKeyExpr {
    constructor(private key: string) {
    }

    public getType(): ContextKeyExprType {
        return ContextKeyExprType.Not;
    }

    public cmp(other: ContextKeyNotExpr): number {
        if (this.key < other.key) {
            return -1;
        }
        if (this.key > other.key) {
            return 1;
        }
        return 0;
    }

    public equals(other: ContextKeyExpr): boolean {
        if (other instanceof ContextKeyNotExpr) {
            return (this.key === other.key);
        }
        return false;
    }

    public evaluate(context: IContext): boolean {
        return (!context.getValue(this.key));
    }

    public normalize(): ContextKeyExpr {
        return this;
    }

    public serialize(): string {
        return '!' + this.key;
    }

    public keys(): string[] {
        return [this.key];
    }
}

export class ContextKeyRegexExpr implements ContextKeyExpr {

    constructor(private key: string, private regexp: RegExp | null) {
        //
    }

    public getType(): ContextKeyExprType {
        return ContextKeyExprType.Regex;
    }

    public cmp(other: ContextKeyRegexExpr): number {
        if (this.key < other.key) {
            return -1;
        }
        if (this.key > other.key) {
            return 1;
        }
        const thisSource = this.regexp ? this.regexp.source : '';
        const otherSource = other.regexp ? other.regexp.source : '';
        if (thisSource < otherSource) {
            return -1;
        }
        if (thisSource > otherSource) {
            return 1;
        }
        return 0;
    }

    public equals(other: ContextKeyExpr): boolean {
        if (other instanceof ContextKeyRegexExpr) {
            const thisSource = this.regexp ? this.regexp.source : '';
            const otherSource = other.regexp ? other.regexp.source : '';
            return (this.key === other.key && thisSource === otherSource);
        }
        return false;
    }

    public evaluate(context: IContext): boolean {
        const value = context.getValue<any>(this.key);
        return this.regexp ? this.regexp.test(value) : false;
    }

    public normalize(): ContextKeyExpr {
        return this;
    }

    public serialize(): string {
        const value = this.regexp
            ? `/${this.regexp.source}/${this.regexp.ignoreCase ? 'i' : ''}`
            : '/invalid/';
        return `${this.key} =~ ${value}`;
    }

    public keys(): string[] {
        return [this.key];
    }
}

export class ContextKeyAndExpr implements ContextKeyExpr {
    public readonly expr: ContextKeyExpr[];

    constructor(expr: Array<ContextKeyExpr | null | undefined>) {
        this.expr = ContextKeyAndExpr._normalizeArr(expr);
    }

    private static _normalizeArr(arr: Array<ContextKeyExpr | null | undefined>): ContextKeyExpr[] {
        let expr: ContextKeyExpr[] = [];

        if (arr) {
            for (let i = 0, len = arr.length; i < len; i++) {
                let e: ContextKeyExpr | null | undefined = arr[i];
                if (!e) {
                    continue;
                }

                e = e.normalize();
                if (!e) {
                    continue;
                }

                if (e instanceof ContextKeyAndExpr) {
                    expr = expr.concat(e.expr);
                    continue;
                }

                expr.push(e);
            }

            expr.sort(cmp);
        }

        return expr;
    }

    public getType(): ContextKeyExprType {
        return ContextKeyExprType.And;
    }

    public equals(other: ContextKeyExpr): boolean {
        if (other instanceof ContextKeyAndExpr) {
            if (this.expr.length !== other.expr.length) {
                return false;
            }
            for (let i = 0, len = this.expr.length; i < len; i++) {
                if (!this.expr[i].equals(other.expr[i])) {
                    return false;
                }
            }
            return true;
        }
        return false;
    }

    public evaluate(context: IContext): boolean {
        for (let i = 0, len = this.expr.length; i < len; i++) {
            if (!this.expr[i].evaluate(context)) {
                return false;
            }
        }
        return true;
    }

    public normalize(): ContextKeyExpr | null {
        if (this.expr.length === 0) {
            return null;
        }

        if (this.expr.length === 1) {
            return this.expr[0];
        }

        return this;
    }

    public serialize(): string {
        if (this.expr.length === 0) {
            return '';
        }
        if (this.expr.length === 1) {
            const normalized = this.normalize();
            if (!normalized) {
                return '';
            }
            return normalized.serialize();
        }
        return this.expr.map((e) => e.serialize()).join(' && ');
    }

    public keys(): string[] {
        const result: string[] = [];
        for (const expr of this.expr) {
            result.push(...expr.keys());
        }
        return result;
    }
}

export interface IContext {
    getValue<T>(key: string): T | undefined;
}

export interface IContextKey<T> {
    set(value: T): void;
    reset(): void;
    get(): T | undefined;
}

export class Context implements IContext {
    private readonly _contextService: ContextService;

    constructor(private readonly obj: any) {
        this._contextService = Application.injector.get(ContextService);
    }

    public getValue<T>(key: string): T | undefined {
        return key in this.obj ? this.obj[key] : this._contextService.getValue(key);
    }
}
